// caiet UC91 — Vizualizare stocuri. Orchestrates the multi-medicine selector,
// the role-aware filter bar, the line chart and the list. All filter state lives
// in the URL query string (ACCS-13). Role differences resolve via the active
// session (UC91-01/05).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { useTranslate } from '@tolgee/react';
import { MudIcon, MudTag, MudTooltip, MudInfoBox } from '@egov-moldova/mud-react';
import { isObserverRole, type Role } from '@simsm/shared-types';
import { EmptyState, RouterLink } from '@simsm/ui-shared';
import { isReporter } from '../../app/access-model';
import { useSession } from '../../hooks/use-session';
import { useSessionRegistrarName } from '../../hooks/use-session-registrar-name';
import { ObserverScopeBanner } from '../AppShell/ObserverScopeBanner';
import { FilterPanel, type FilterState } from './FilterPanel';
import { MedicineMultiSelect, type SelectedMedicine } from './MedicineMultiSelect';
import { registrarFacetLabel } from './registrar-label';
import { StockDataTable } from './StockDataTable';
import { StockLineChart } from './StockLineChart';
import {
  defaultRange,
  dimensionFor,
  isValidPeriod,
  levelsFor,
  parseCsv,
  resolveLevel,
} from './stock-filters';
import { CHART_SERIES_LIMIT, chartedKeys } from './stock-series';
import {
  imspSiteOptions,
  scopeOptions,
  useImspRegistrars,
  useImspSites,
  useStockRegions,
  useStockScope,
} from './useStockScope';
import {
  isTimeseriesRefusal,
  useStockTimeseries,
  type StockCriterion,
  type TimeseriesFilters,
} from './useStockTimeseries';

// Where each criterion the server may drop lives in this page's address.
const ADDRESS_KEY: Record<StockCriterion, string> = {
  dimension: 'level',
  registrarIds: 'registrarIdnos',
  siteIds: 'siteIds',
  regionIds: 'regionIds',
  localityIds: 'localityIds',
  lots: 'lots',
};

// The national view: everything above a single Registrar. caiet UC61-03 keeps
// the observer names open, so this is a predicate, not a list.
function seesEveryRegistrar(role: Role): boolean {
  return role === 'SYSTEM_ADMIN' || isObserverRole(role);
}

export function StockChartTable(): JSX.Element {
  const { t } = useTranslate();
  const session = useSession();
  const registrarName = useSessionRegistrarName();
  const [searchParams, setSearchParams] = useCriteriaParams();
  const fallback = useMemo(() => defaultRange(), []);

  const role: Role = session.data?.activeRole ?? 'SYSTEM_ADMIN';
  const levels = levelsFor(role);
  const seesEveryone = seesEveryRegistrar(role);
  const canFilterLocality = role === 'SYSTEM_ADMIN';

  const medicineCodes = parseCsv(searchParams.get('medicineCodes'));
  const labelParam = searchParams.get('labels');
  const labels = labelParam ? labelParam.split('|') : [];
  const selectedMedicines: SelectedMedicine[] = medicineCodes.map((code, index) => ({
    code,
    label: labels[index] ?? code,
  }));

  const levelParam = searchParams.get('level');
  const filterState: FilterState = {
    from: searchParams.get('from') ?? fallback.from,
    to: searchParams.get('to') ?? fallback.to,
    level: resolveLevel(levelParam, role),
    regionIds: parseCsv(searchParams.get('regionIds')),
    localityIds: parseCsv(searchParams.get('localityIds')),
    registrarIdnos: parseCsv(searchParams.get('registrarIdnos')),
    siteIds: parseCsv(searchParams.get('siteIds')),
    lots: parseCsv(searchParams.get('lots')),
  };

  // Parity A-32. The register's facets answer this screen's filter lists for the
  // roles UC93-01 grants the register; for an Observer they answered `403
  // REGISTER_FORBIDDEN` on every load and the panel fell silent. The Observer's
  // one Registrar entitlement (UC91-12) has a source of its own.
  const scope = useStockScope(role);
  const facets = scope.data ?? [];
  const imspRegistrars = useImspRegistrars(isObserverRole(role));
  const catalogRegions = useStockRegions(isObserverRole(role));
  // UC91-13 / CF-23: the Observer's locations are the IMSP ones, from their own source.
  const imspSites = useImspSites(isObserverRole(role));
  const options = useMemo(() => {
    const scoped = scopeOptions(facets, {
      regionIds: filterState.regionIds,
      localityIds: filterState.localityIds,
      registrarIdnos: filterState.registrarIdnos,
    });
    const own = isObserverRole(role)
      ? {
          ...scoped,
          regions: (catalogRegions.data ?? []).map((item) => ({
            value: item.id,
            label: item.name,
          })),
          registrars: (imspRegistrars.data ?? []).map((item) => ({
            value: item.idno,
            label: registrarFacetLabel(item.name, item.idno),
          })),
          sites: imspSiteOptions(imspSites.data ?? [], {
            registrarIdnos: filterState.registrarIdnos,
            regionIds: filterState.regionIds,
          }),
        }
      : scoped;
    // UC91-13: an inactive site is offered with its history, and says so.
    return {
      ...own,
      sites: own.sites.map((site) =>
        site.inactive
          ? { ...site, label: t('common.filter.inactive_site', { name: site.label }) }
          : site,
      ),
    };
  }, [
    facets,
    imspRegistrars.data,
    imspSites.data,
    catalogRegions.data,
    role,
    filterState.regionIds,
    filterState.localityIds,
    filterState.registrarIdnos,
    t,
  ]);

  // UC91-11 (regions) and UC91-12 (Registrar) are two entitlements, and one
  // flag used to gate both: an Observer with a Registrar list and no region
  // list lost the filter the caiet gives them along with the one it cannot fill.
  const canFilterRegion = seesEveryone && options.regions.length > 0;
  const canFilterRegistrar = seesEveryone && options.registrars.length > 0;

  // UC91-06: a period that is not two calendar days in order is not asked for; the
  // screen says what is wrong instead (TC-UC91-06-03).
  const periodOk = isValidPeriod(filterState.from, filterState.to);

  // ACCS-13 / CF-52: the address's criteria go to the server as they are, and the
  // server drops the ones the active role or Registrar may not use, says which and
  // logs the attempt. The level is the one the screen resolves itself: it cannot
  // draw a level the role does not have.
  const queryFilters: TimeseriesFilters = {
    medicineCodes,
    from: filterState.from,
    to: filterState.to,
    // UC91-07: the interval decides the bucket, so the screen never names one.
    dimension: dimensionFor(filterState.level),
    registrarIds: filterState.registrarIdnos,
    siteIds: filterState.siteIds,
    regionIds: filterState.regionIds,
    localityIds: filterState.localityIds,
    lots: filterState.lots,
  };

  const timeseries = useStockTimeseries(queryFilters);

  // What was dropped is said once and taken out of the address, so the panel, a
  // reload and a new bookmark stop carrying it. The notice outlives the rewrite: it
  // stays until the reader closes it or changes a criterion.
  const [criteriaDropped, setCriteriaDropped] = useState(false);
  const droppedByServer = timeseries.data?.droppedCriteria;
  const levelDropped =
    session.data !== undefined && levelParam !== null && levelParam !== filterState.level;
  useEffect(() => {
    const dropped = droppedByServer ?? [];
    if (!levelDropped && dropped.length === 0) return;
    setCriteriaDropped(true);
    const next = new URLSearchParams(searchParams);
    if (levelDropped) next.delete('level');
    for (const { criterion, values } of dropped) {
      const key = ADDRESS_KEY[criterion];
      if (criterion === 'dimension') {
        next.delete(key);
        continue;
      }
      const kept = parseCsv(next.get(key)).filter((value) => !values.includes(value));
      if (kept.length === 0) next.delete(key);
      else next.set(key, kept.join(','));
    }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [droppedByServer, levelDropped, searchParams, setSearchParams]);

  // CF-66: the series the reader has unticked in the legend. The chart draws the
  // first CHART_SERIES_LIMIT of the others; the list keeps them all
  // (docs/shape-uc-91-series-and-movements.md §2 D5).
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const allSeries = timeseries.data?.series;
  const charted = useMemo(() => chartedKeys(allSeries ?? [], hidden), [allSeries, hidden]);
  const toggleSeries = useCallback((key: string, shown: boolean): void => {
    setHidden((current) => {
      const next = new Set(current);
      if (shown) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // The server resolves the same identity the picker showed, so the table reads
  // it from the response first: a link opened with `medicineCodes` and no
  // `labels` (ACCS-13 lets anyone hand that URL over) would otherwise fall back
  // to the bare code in the Medicament column.
  const labelFor = useCallback(
    (code: string): string =>
      timeseries.data?.series.find((s) => s.medicineCode === code)?.label ??
      selectedMedicines.find((medicine) => medicine.code === code)?.label ??
      code,
    [selectedMedicines, timeseries.data],
  );

  // A chip is a selected code, and its colour is the colour of the series that code
  // is drawn in: two codes of one unique medicine share one line (CF-34).
  const colorFor = useCallback(
    (code: string): string => {
      const series = timeseries.data?.series.find((s) => s.medicineCodes.includes(code));
      return series?.color ?? 'var(--color-text-base-tertiary)';
    },
    [timeseries.data],
  );

  // Every caller is the reader changing a criterion, which retires the notice.
  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void): void => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setCriteriaDropped(false);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const addMedicine = useCallback(
    (medicine: SelectedMedicine): void => {
      writeParams((params) => {
        params.set('medicineCodes', [...medicineCodes, medicine.code].join(','));
        params.set('labels', [...selectedMedicines.map((m) => m.label), medicine.label].join('|'));
      });
      // A medicine picked again is drawn again, whatever the legend said before.
      setHidden(
        (current) => new Set([...current].filter((key) => !key.startsWith(`${medicine.code}|`))),
      );
    },
    [medicineCodes, selectedMedicines, writeParams],
  );

  const removeMedicine = useCallback(
    (code: string): void => {
      writeParams((params) => {
        const keep = selectedMedicines.filter((m) => m.code !== code);
        if (keep.length === 0) {
          params.delete('medicineCodes');
          params.delete('labels');
          return;
        }
        params.set('medicineCodes', keep.map((m) => m.code).join(','));
        params.set('labels', keep.map((m) => m.label).join('|'));
      });
    },
    [selectedMedicines, writeParams],
  );

  const patchFilters = useCallback(
    (patch: Partial<FilterState>): void => {
      writeParams((params) => {
        for (const [key, value] of Object.entries(patch)) {
          if (Array.isArray(value)) {
            if (value.length === 0) params.delete(key);
            else params.set(key, value.join(','));
          } else if (value !== undefined && value !== '') {
            params.set(key, String(value));
          }
        }
      });
    },
    [writeParams],
  );

  const resetFilters = useCallback((): void => {
    writeParams((params) => {
      for (const key of [
        'from',
        'to',
        'level',
        'regionIds',
        'localityIds',
        'registrarIdnos',
        'siteIds',
        'lots',
      ]) {
        params.delete(key);
      }
    });
  }, [writeParams]);

  // The server refuses a selection above its own ceiling rather than trimming it,
  // so the ceiling is read off the refusal instead of being restated here: a
  // number written twice is a number that drifts.
  const refusal = isTimeseriesRefusal(timeseries.error) ? timeseries.error : null;

  return (
    <section className="simsm-uc91">
      <header className="simsm-uc91__header">
        <h1 className="simsm-uc91__title">{t('stockChart.title')}</h1>
        {/* Owner decision D5. Stocuri.tsx:218 gives this head a lede only when the role is fixed
            to one Registrar, and names it: the System Administrator and the observers get the
            title alone. The sentence that stood here before was in no drawing. */}
        {registrarName ? (
          <p className="simsm-uc91__lede">
            {t('stockChart.lede.registrar', { name: registrarName })}
          </p>
        ) : null}
        {/* FR-026. Expiry is a column of the stock view in the wireframe, so `expiring` is a menu
            entry for the Observers only; the dashboard landing removed on the owner's 2026-08-31
            ruling was the administrator's only in-app route to that screen. The link belongs where
            the wireframe already puts the subject. Relative, so it keeps the role segment. */}
        <RouterLink className="simsm-uc91__expiring-link" to="../expiring" size="sm">
          {t('stockChart.link.expiring')}
        </RouterLink>
      </header>

      <ObserverScopeBanner />

      {criteriaDropped ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="warning"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setCriteriaDropped(false)}
        >
          {t('stockChart.criteria_dropped')}
        </MudInfoBox>
      ) : null}

      <div className="simsm-uc91__controls">
        <div className="simsm-uc91__medicines">
          {/* A-13 · the drawing marks the field required and hangs an info icon
              off the label (`Stocuri.tsx:59`, `InfoTip id="tip-med-stoc"`). The
              tip says what the box matches on, which is the one thing a reader
              cannot infer from the placeholder alone. */}
          <span className="simsm-uc91__controls-label">
            {t('stockChart.medicine.label')}
            <span aria-hidden="true"> *</span>
            <MudTooltip content={t('stockChart.medicine.hint')} position="top">
              {/* Unslotted, this icon lands in the bubble instead of on the
                  trigger, and the tip renders 0x0 — see SectionTip in
                  MedicineFormPage.tsx. */}
              <MudIcon slot="trigger" name="circle-info" size={16} />
            </MudTooltip>
          </span>
          <MedicineMultiSelect
            selected={selectedMedicines}
            colorFor={colorFor}
            onAdd={addMedicine}
            onRemove={removeMedicine}
          />
        </div>

        <FilterPanel
          state={filterState}
          levels={levels}
          canFilterRegion={canFilterRegion}
          canFilterRegistrar={canFilterRegistrar}
          canFilterLocality={canFilterLocality && options.localities.length > 0}
          options={options}
          onChange={patchFilters}
          onReset={resetFilters}
        />
      </div>

      {medicineCodes.length === 0 ? (
        /* The wireframe's own empty for this screen (Stocuri.tsx:224): it names
           what will appear and what to search by, where the info bar this
           replaced said only "select one or more medicines". */
        <EmptyState
          icon="chart"
          title={t('stockChart.empty.no_medicine')}
          body={t('stockChart.empty.no_medicine_body')}
        />
      ) : !periodOk ? (
        /* The wireframe's own answer to a period it cannot read (Stocuri.tsx, `perioadaOk`):
           what is wrong and how to fix it, not the failed-load message. */
        <EmptyState
          icon="calendar"
          title={t('stockChart.empty.invalid_period')}
          body={t('stockChart.empty.invalid_period_body')}
        />
      ) : timeseries.isLoading ? (
        <p className="simsm-uc91__loading">{t('stockChart.loading')}</p>
      ) : timeseries.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {refusal?.code === 'TOO_MANY_MEDICINE_CODES' && refusal.limit !== undefined
            ? t('stockChart.too_many_medicines', {
                selected: medicineCodes.length,
                limit: refusal.limit,
              })
            : t('stockChart.error')}
        </MudInfoBox>
      ) : timeseries.data ? (
        <>
          <div className="simsm-uc91__chart-head">
            <h2 className="simsm-uc91__chart-title">{t('stockChart.chart.title')}</h2>
            <MudTag
              variant="info"
              type="subtle"
              size="md"
              label={t(`stockChart.auto.${timeseries.data.granularity}`)}
            />
          </div>
          <StockLineChart
            buckets={timeseries.data.buckets}
            series={timeseries.data.series}
            charted={charted}
            onToggle={toggleSeries}
            grouped={filterState.level === 'region' || filterState.level === 'locality'}
          />

          {/* CF-66 / TC-UC91-03-04: past the chart's ceiling the reader is told where
              the other series are. */}
          {timeseries.data.series.length > CHART_SERIES_LIMIT ? (
            // Both notes below describe the answer the reader's last query got, so
            // they are announced as it arrives, as AGE's notification did with its
            // own role="status" (059 V-087 sweep).
            <MudInfoBox emphasis="strong" role="status" variant="info">
              {t('stockChart.series_table_only', { limit: CHART_SERIES_LIMIT })}
            </MudInfoBox>
          ) : null}
          {/* D4: the server lists the newest rows of the window and says how many
              matched; the screen says when it shows fewer. */}
          {timeseries.data.movementCount > timeseries.data.movements.length ? (
            <MudInfoBox emphasis="strong" role="status" variant="info">
              {t('stockChart.movements.truncated', {
                shown: timeseries.data.movements.length,
                total: timeseries.data.movementCount,
              })}
            </MudInfoBox>
          ) : null}

          <StockDataTable
            level={filterState.level}
            granularity={timeseries.data.granularity}
            series={timeseries.data.series}
            movements={timeseries.data.movements}
            charted={charted}
            canSeeEveryRegistrar={seesEveryone}
            canCorrect={isReporter(role)}
            labelFor={labelFor}
          />
        </>
      ) : null}
    </section>
  );
}
